// Read-only store map for the public /trends pages: merchant_map_controller's
// mechanics (bbox in the constructor so the first frame is already framed,
// re-fit once idle) plus server-colored dots — the payload's ci indexes the
// colors array, so the client does no ranking. circle-sort-key keeps the gray
// "Other" dots under the highlighted fleets.
// The library is import()ed inside the mount: whenVisible already defers the
// map's WORK below the fold, this defers its PARSE too — MapLibre lands in a
// split chunk that only loads when a map approaches the viewport. The CSS
// import stays static so the styles remain part of public.css.
import "maplibre-gl/dist/maplibre-gl.css"
import { whenVisible } from "./lazy"
import { fetchPayload } from "./payload_cache"
import { createTip } from "./tooltip"

export const STYLE_URL = "https://maps.iceflower.ai/styles/positron/style.json"

export function initTrendsMaps() {
  document.querySelectorAll("[data-trends-map]").forEach((el) => {
    whenVisible(el, () => mountMap(el).catch(() => {}))
  })
}

async function mountMap(el) {
  const { default: maplibregl } = await import("maplibre-gl")
  const bbox = el.dataset.bbox ? JSON.parse(el.dataset.bbox) : null
  const map = new maplibregl.Map({
    container: el,
    style: STYLE_URL,
    interactive: false,
    attributionControl: false,
    ...initialView(bbox)
  })
  // OSM-derived basemap on a public page: ODbL wants credit. The tile
  // source's TileJSON already carries the linked OpenMapTiles/OSM string —
  // the compact (i) control surfaces it as-is.
  map.addControl(new maplibregl.AttributionControl({ compact: true }))

  const [payload] = await Promise.all([
    fetchPayload(el.dataset.url),
    new Promise((resolve) => map.on("load", resolve))
  ])
  if (!payload) return

  addDots(map, payload)
  addTooltip(el, map)

  // Position the map on its bbox from the very first frame; without this the
  // first paint is maplibre's default world view before fitBounds kicks in.
  map.once("idle", () => {
    map.resize()
    fitBounds(map, payload.bbox || bbox)
  })
}

function initialView(bbox) {
  if (!bbox || bbox.length < 4) return {}

  const [minLon, minLat, maxLon, maxLat] = bbox
  return {
    bounds: [[minLon, minLat], [maxLon, maxLat]],
    fitBoundsOptions: { padding: 30, maxZoom: 11 }
  }
}

function fitBounds(map, bbox) {
  if (!bbox || bbox.length < 4) return

  const [minLon, minLat, maxLon, maxLat] = bbox
  map.fitBounds(
    [[minLon, minLat], [maxLon, maxLat]],
    { padding: 30, maxZoom: 11, animate: false }
  )
}

function addDots(map, payload) {
  const features = payload.dots.map(([lon, lat, ci, ni]) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: { ci, color: payload.colors[ci] || payload.colors[0], name: (payload.names || [])[ni] || "" }
  }))

  map.addSource("stores", {
    type: "geojson",
    data: { type: "FeatureCollection", features }
  })

  map.addLayer({
    id: "store-dots",
    type: "circle",
    source: "stores",
    // Sort highlighted-vs-gray only: gray "Other" dots sit underneath, but
    // named slots stay unranked among themselves — sorting by raw ci let the
    // highest slot (violet) paint over every cluster on the category maps.
    layout: { "circle-sort-key": ["case", [">", ["get", "ci"], 0], 1, 0] },
    paint: {
      "circle-radius": ["case", [">", ["get", "ci"], 0], 3, 2.5],
      "circle-color": ["get", "color"],
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": ["case", [">", ["get", "ci"], 0], 0.8, 0.5],
      "circle-opacity": 0.85
    }
  })
}

// Hover tooltip naming the store's merchant — the shared pill, no Popup
// machinery. Layer-scoped mouse events fire even though the map itself is
// non-interactive (interactive: false only drops the pan/zoom handlers,
// not the event system).
function addTooltip(el, map) {
  const tip = createTip(el)

  map.on("mousemove", "store-dots", (e) => {
    const name = e.features && e.features[0] && e.features[0].properties.name
    if (name) {
      tip.show(e.point.x, e.point.y, name)
    } else {
      tip.hide()
    }
  })
  map.on("mouseleave", "store-dots", () => tip.hide())
}
